import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, ArrowRight, Loader2 } from 'lucide-react';
import { useDashboardStore } from '../../stores/dashboardStore';
import ProgressOverview from './ProgressOverview';
import SkillsAnalysis from './SkillsAnalysis';
import JobRecommendations from './JobRecommendations';
import CareerAnalysis from './CareerAnalysis';
import CoursesSection from './CoursesSection';
import { useAuthStore } from '../../stores/authStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import toast from 'react-hot-toast';
import { useAnalysisStore } from '../../stores/analysisStore';
import { validateCareerAnalysis } from '../../types/analysis';
import { fetchFromSupabase } from '../../config/supabase';
import { supabaseClient } from '../../config/supabase';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { plan } = useSubscriptionStore();
  const { 
    fetchUserDashboard, 
    updateProgress, 
    loading: dashboardLoading,
    error: dashboardError,
    initialized,
    setInitialized,
    resetDashboard
  } = useDashboardStore((state) => state);
  const { 
    analysis, 
    setAnalysis, 
    analysisLoading,
    analysisError,
    saveAnalysisToDatabase,
    fetchAnalysisFromDatabase
  } = useAnalysisStore();

  // --- Logowanie odczytu quizAnswers ---
  const quizStorageKey = `quiz_${user?.id}`;
  const rawQuizAnswers = localStorage.getItem(quizStorageKey);
  console.log(`[Dashboard] Odczytano z localStorage (klucz: ${quizStorageKey}):`, rawQuizAnswers ? rawQuizAnswers.substring(0, 100) + '...' : 'null');
  const quizAnswers = rawQuizAnswers ? JSON.parse(rawQuizAnswers) : null;
  console.log('[Dashboard] Sparsowane quizAnswers:', quizAnswers);
  // --- Koniec Logowania ---

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    let mounted = true;
    
    const fetchInitialData = async () => {
      console.log('[Dashboard] Rozpoczęto fetchInitialData');
      
      // --- Logowanie stanu początkowego ---
      const analysisFromStore = useAnalysisStore.getState().analysis;
      const rawAnalysisFromLocalStorage = localStorage.getItem(`analysis_${user.id}`);
      console.log('[Dashboard] Stan początkowy: analysis w store:', analysisFromStore ? 'ISTNIEJE' : 'null', '| analysis w localStorage:', rawAnalysisFromLocalStorage ? 'ISTNIEJE' : 'null');
      // --- Koniec Logowania ---

      if (analysisFromStore && mounted) {
        console.log('[Dashboard] Znaleziono analizę w analysisStore.');
        if (!initialized) {
          await updateProgress({ quizCompleted: true, analysisGenerated: true, profileCompleted: 60 });
          setInitialized(true);
        }
        return;
      }
      
      console.log('[Dashboard] Brak analizy w analysisStore, sprawdzam localStorage i bazę...');
      useAnalysisStore.setState({ analysisLoading: true, analysisError: null });

      try {
        // --- Logowanie odczytu analizy z localStorage ---
        const localAnalysisString = localStorage.getItem(`analysis_${user.id}`);
        console.log(`[Dashboard] Odczytano analysis z localStorage (klucz: analysis_${user.id}):`, localAnalysisString ? localAnalysisString.substring(0, 100) + '...' : 'null');
        // --- Koniec Logowania ---

        if (localAnalysisString && mounted) {
          console.log('[Dashboard] Znaleziono analizę w localStorage.');
          try {
            const parsedAnalysis = JSON.parse(localAnalysisString);
            if (validateCareerAnalysis(parsedAnalysis)) {
              console.log('[Dashboard] Analiza z localStorage poprawna, ustawiam stan.');
              useAnalysisStore.setState({ analysis: parsedAnalysis, analysisLoading: false });
              if (!initialized) {
                await updateProgress({ quizCompleted: true, analysisGenerated: true, profileCompleted: 60 });
                setInitialized(true);
              }
              saveAnalysisToDatabase(user.id).catch(err => console.warn('[Dashboard] Nie udało się zapisać analizy z localStorage do bazy:', err));
              return;
            } else {
              console.warn('[Dashboard] Analiza z localStorage nie przeszła walidacji, usuwam.');
              localStorage.removeItem(`analysis_${user.id}`);
            }
          } catch (parseError) {
            console.error('[Dashboard] Błąd parsowania analizy z localStorage, usuwam:', parseError);
            localStorage.removeItem(`analysis_${user.id}`);
          }
        } else {
          console.log('[Dashboard] Brak analizy w localStorage.');
        }

        // Jeśli nie znaleziono w localStorage lub była niepoprawna, pobierz z bazy
        if (!useAnalysisStore.getState().analysis && mounted) { // Sprawdź ponownie stan store
           console.log('[Dashboard] Próba pobrania analizy z bazy danych (fetchAnalysisFromDatabase)...');
           const dbAnalysis = await fetchAnalysisFromDatabase(user.id); // Ta funkcja już loguje i ustawia stan

           if (dbAnalysis && mounted) {
             console.log('[Dashboard] Analiza pobrana i ustawiona przez fetchAnalysisFromDatabase.');
             if (!initialized) {
               await updateProgress({ quizCompleted: true, analysisGenerated: true, profileCompleted: 60 });
               setInitialized(true);
             }
             return; // Mamy analizę z DB
           } else if (mounted) {
              console.log('[Dashboard] fetchAnalysisFromDatabase nie zwrócił analizy.');
              // Stan błędu lub braku analizy jest już ustawiony w fetchAnalysisFromDatabase
           }
        } else if (mounted) {
           console.log('[Dashboard] Analiza została już ustawiona (prawdopodobnie z localStorage).');
           // Upewnij się, że stan initialized jest poprawny
           if (!initialized) {
             await updateProgress({ quizCompleted: true, analysisGenerated: true, profileCompleted: 60 });
             setInitialized(true);
           }
           return;
        }

        // --- Logika po nieudanych próbach pobrania analizy ---
        if (mounted && !useAnalysisStore.getState().analysis) { // Sprawdź stan store na końcu
           console.log('[Dashboard] Finalnie: Brak analizy w store, localStorage i DB.');
           if (quizAnswers) { // Użyj zmiennej z początku komponentu
             console.log('[Dashboard] Quiz wypełniony (wg localStorage), ale brak analizy.');
             if (!initialized) {
               await updateProgress({ quizCompleted: true, analysisGenerated: false, profileCompleted: 30 });
               setInitialized(true);
             }
           } else {
             console.log('[Dashboard] Quiz nie był wypełniony (wg localStorage).');
             if (!initialized) {
               await updateProgress({ quizCompleted: false, analysisGenerated: false, profileCompleted: 0 });
               setInitialized(true);
             }
           }
        }
        // --- Koniec Logiki ---

     } catch (error) { // Teraz ten catch jest w poprawnym miejscu
       console.error('[Dashboard] Nieoczekiwany błąd podczas fetchInitialData:', error); // Log Error D2
       if (mounted) {
         useAnalysisStore.setState({ analysisError: 'Nie udało się załadować danych dashboardu', analysisLoading: false });
         toast.error('Błąd podczas ładowania danych dashboardu');
       }
     } finally {
        if (mounted) {
          useAnalysisStore.setState({ analysisLoading: false });
          if (!initialized) {
             console.log('[Dashboard] Finalne ustawienie initialized na true w finally.');
             setInitialized(true);
          }
        }
        console.log('[Dashboard] Zakończono fetchInitialData');
      }
    };
    
    if (!initialized || !analysis) {
       fetchInitialData();
    } else {
       console.log('[Dashboard] Inicjalizacja pominięta (initialized=true, analysis istnieje).');
    }
    
    return () => {
      mounted = false;
      console.log('[Dashboard] Komponent odmontowany.');
    };
  }, [user, initialized, setInitialized, updateProgress, fetchAnalysisFromDatabase, saveAnalysisToDatabase, navigate]);

  useEffect(() => {
    if (!quizAnswers && initialized && analysis) {
      console.log('[Dashboard] Resetowanie dashboardu z powodu braku quizAnswers.');
      resetDashboard();
      useAnalysisStore.getState().clearAnalysis();
    }
  }, [quizAnswers, initialized, analysis, resetDashboard]);

  if (analysisLoading || dashboardLoading || !initialized) {
    console.log(`[Dashboard] Renderowanie stanu ładowania (analysisLoading: ${analysisLoading}, dashboardLoading: ${dashboardLoading}, initialized: ${initialized})`);
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Ładowanie danych...</p>
        </motion.div>
      </div>
    );
  }

  if (analysisError || dashboardError) {
    console.log(`[Dashboard] Renderowanie stanu błędu (analysisError: ${analysisError}, dashboardError: ${dashboardError})`);
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="text-red-600 mb-6">
            <Brain className="w-12 h-12 mx-auto mb-4" />
            <p className="text-lg font-medium">{analysisError || dashboardError}</p>
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-all transform hover:scale-105"
          >
            Spróbuj ponownie
          </button>
        </motion.div>
      </div>
    );
  }

  if (quizAnswers && !analysis && !analysisLoading && initialized) {
     console.log('[Dashboard] Renderowanie komunikatu "Quiz wypełniony, brak analizy" (quizAnswers=true, analysis=null)');
    return (
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        className="min-h-screen bg-gray-50 py-16"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl p-8 text-center"
          >
            <Brain className="w-16 h-16 text-purple-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Witaj w Kariera z AI!
            </h2>
            <p className="text-gray-600 mb-8">
              Wygląda na to, że wypełniłeś quiz, ale analiza nie została jeszcze wygenerowana lub załadowana. Spróbuj odświeżyć stronę lub skontaktuj się z pomocą techniczną, jeśli problem będzie się powtarzał.
            </p>
             <button
              onClick={() => {
                setInitialized(false);
                useAnalysisStore.setState({ analysis: null });
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl 
                font-medium hover:bg-blue-700 transition-all transform hover:scale-105 mr-4"
            >
              Odśwież dane
            </button>
            <button
              onClick={() => navigate('/#quiz')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl 
                font-medium hover:bg-purple-700 transition-all transform hover:scale-105"
            >
              Wypełnij quiz ponownie
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </motion.div>
    );
  }
  
  if (!quizAnswers && !analysis && !analysisLoading && initialized) {
    console.log('[Dashboard] Renderowanie komunikatu dla nowych użytkowników (quizAnswers=null, analysis=null)');
     return (
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        className="min-h-screen bg-gray-50 py-16"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl p-8 text-center"
          >
            <Brain className="w-16 h-16 text-purple-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Witaj w Kariera z AI!
            </h2>
            <p className="text-gray-600 mb-8">
              Aby otrzymać spersonalizowane rekomendacje kariery, wypełnij krótki quiz predyspozycji.
              To zajmie tylko kilka minut!
            </p>
            <button
              onClick={() => navigate('/#quiz')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl 
                font-medium hover:bg-purple-700 transition-all transform hover:scale-105"
            >
              Wypełnij quiz
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  console.log('[Dashboard] Renderowanie głównego widoku dashboardu (analysis istnieje).');
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <CareerAnalysis analysis={analysis} />
              </motion.div>
            </div>
          </div>
          
          <div>
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <ProgressOverview />
              </div>
              <JobRecommendations userAnswers={quizAnswers} analysis={analysis} />
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <SkillsAnalysis analysis={analysis} />
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-8">
          <CoursesSection analysis={analysis} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
